/**
 * THE SHARED PIECES OF EVERY STATEMENT OF ADVICE (3 Oct 2026) — the aged care, Support at Home and
 * module documents are three HTML builders that had drifted: pastel infills the owner called
 * "old" and "shit", a four-row "situation" table, a projection table with no picture, and an
 * appendix that listed the client's assets but none of the rates the advice rested on (the aged
 * care one even hand-typed 2023-24 tax brackets). One kit, three documents:
 *
 *   - SOA_THEME_CSS — the app's own language on paper: white cards, slate borders, a coloured rule
 *     on one side of a callout, a coloured rule across the top of a figure tile, tabular numbers,
 *     and no pastel fills anywhere. Appended after each document's stylesheet so it wins.
 *   - situationFrom / renderSituationHtml — "Your situation today" as the four numbers the
 *     analyser's step 2 shows, then the household, what is owned (by type, with its share), what
 *     comes in and goes out, and what is owed.
 *   - renderLineChart / renderProjectionCharts — an SVG line chart per measure (net worth, Age
 *     Pension, cashflow; the plan against the baseline where one exists), built to the dataviz
 *     method: 2px lines, >= 8px markers with a surface ring, recessive grid, direct end labels, a
 *     legend only for two series, text in ink never in the series colour, a hover crosshair with
 *     one tooltip for every series (wired by soaBookletScript.ts — the popup inherits a CSP that
 *     forbids inline script). Palette #2563eb / #ea580c validated with the skill's checker.
 *   - renderAssumptionsAppendixHtml — the Assumptions page's own sections (assumptionSections.ts),
 *     the module's relevant ones open, the rest collapsed.
 *   - renderComparisonTable — every strategy modelled, sortable by estate, pension or cashflow.
 *   - renderToc — a clickable map of the sections, so a client can jump rather than page.
 *
 * Everything here is a pure function of plain data, so a stored snapshot re-renders identically
 * for the licensee view.
 */
import { formatCurrency } from '../../utils/index.ts';
import type { AssumptionSection } from './assumptionSections.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

const esc = (value: unknown): string =>
    String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
const money = (n: number): string => formatCurrency(n);
const prettify = (key: string): string => key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/* ─── The theme ─────────────────────────────────────────────────────────────────────────────── */

/** Series colours, validated (light surface): plan blue, baseline orange, a third green if ever needed. */
export const SOA_SERIES_COLORS = ['#2563eb', '#ea580c', '#059669'] as const;

export const SOA_THEME_CSS = `
/* ── AgedCareWise document theme (soaKit.ts): white surfaces, one coloured rule, no pastel fills ── */
:root{--rule-blue:#2563eb;--rule-amber:#f59e0b;--rule-green:#10b981;--rule-rose:#f43f5e;--rule-slate:#64748b;--ink-2:#334155}
.book{background:#f8fafc}
.card{border-radius:16px;box-shadow:0 10px 30px rgba(15,23,42,.06)}
.card::before{height:4px;background:linear-gradient(90deg,#2563eb,#14b8a6)}
.h2{font-size:28px;letter-spacing:-.02em;background:linear-gradient(90deg,#1e3a8a,#2563eb);-webkit-background-clip:text;background-clip:text;color:transparent}
.h3{font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#64748b;margin:22px 0 10px}
.lede{font-size:16px;color:#1e293b}
.kpi,.kpi--gain,.kpi--warn{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:16px 14px 14px;position:relative;overflow:hidden;box-shadow:0 1px 2px rgba(15,23,42,.04)}
.kpi::before{content:'';position:absolute;left:0;right:0;top:0;height:4px;background:var(--rule-slate)}
.kpi--gain::before{background:#34d399}.kpi--warn::before{background:#fb7185}
.kpi--gain .value{color:#047857}.kpi--warn .value{color:#be123c}
.kpi .value{font-size:26px;font-weight:800;letter-spacing:-.01em;font-variant-numeric:tabular-nums}
.kpi .label{font-size:12px;color:#475569;text-transform:none;letter-spacing:0;font-weight:600}
.callout,.callout--blue,.callout--amber,.callout--slate,.callout--green{background:#fff;border:1px solid #e2e8f0;border-left:4px solid var(--rule-blue);border-radius:12px;box-shadow:0 1px 2px rgba(15,23,42,.04)}
.callout--amber{border-left-color:var(--rule-amber)}.callout--green{border-left-color:var(--rule-green)}.callout--slate{border-left-color:var(--rule-slate)}
.callout-title{color:#0f172a}
.oneLine{font-size:21px;font-weight:800;line-height:1.35;color:#0f172a}
.imp-card{background:#fff;border:1px solid #e2e8f0;border-left:4px solid var(--rule-blue);border-radius:12px}
.imp-title{color:#0f172a}
.scope-in,.scope-out{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:16px}
.scope-in{border-left:4px solid var(--rule-green)}.scope-out{border-left:4px solid var(--rule-rose)}
.scope-in h4,.scope-out h4{color:#475569}
.piece{background:#fff;border:1px solid #e2e8f0;border-left:4px solid var(--rule-blue)}
.piece-amt{color:#1d4ed8}
.opt-card{background:#fff;border:1px solid #e2e8f0;border-left:4px solid #cbd5e1}
.opt-card--selected,.opt-card--conditional{background:#fff;border-color:#e2e8f0;border-left:4px solid var(--rule-blue);box-shadow:0 10px 30px rgba(37,99,235,.10)}
.opt-card-head .opt-tag{background:#fff;border:1px solid #bfdbfe;color:#1d4ed8}
.pill-not-selected{background:#fff;border:1px solid #cbd5e1;color:#475569}
.pill-selected{background:var(--rule-blue);color:#fff}
.estate-alert{background:#fff;border:1px solid #e2e8f0;border-left:4px solid var(--rule-rose);border-radius:12px}
details,.factSection{background:#fff;border:1px solid #e2e8f0}
details summary{cursor:pointer}
th{background:#fff;border-bottom:2px solid #e2e8f0;color:#475569}
.table-proj tbody tr.proj-tr-data.proj-zebra{background:#fff}
.table-proj tbody tr.proj-tr-data:nth-child(even){background:#f8fafc}
.td-num,.soa-num{font-variant-numeric:tabular-nums}
.riskList li strong{color:#0f172a}
.muted{color:#64748b}
/* stat tiles — the analyser's step-2 tiles */
.soa-tiles{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:12px 0 6px}
.soa-tile{position:relative;overflow:hidden;border:1px solid #e2e8f0;border-radius:12px;background:#fff;padding:18px 14px 14px;box-shadow:0 1px 2px rgba(15,23,42,.04)}
.soa-tile::before{content:'';position:absolute;left:0;right:0;top:0;height:4px;background:var(--rule-slate)}
.soa-tile--good::before{background:#34d399}.soa-tile--bad::before{background:#fb7185}.soa-tile--family::before{background:#0290f5}.soa-tile--cost::before{background:#64748b}
.soa-tile .t-label{font-size:13px;font-weight:600;color:#475569}
.soa-tile .t-value{font-size:26px;font-weight:800;letter-spacing:-.01em;margin-top:6px;font-variant-numeric:tabular-nums;color:#0f172a}
.soa-tile--good .t-value{color:#047857}.soa-tile--bad .t-value{color:#be123c}
.soa-tile .t-unit{font-size:13px;font-weight:600;color:#64748b;margin-left:4px}
.soa-tile .t-cap{font-size:13px;color:#64748b;margin-top:6px;line-height:1.4}
/* situation */
.soa-situ{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:8px}
.soa-panel{border:1px solid #e2e8f0;border-radius:12px;background:#fff;padding:14px 16px;box-shadow:0 1px 2px rgba(15,23,42,.04)}
.soa-panel h4{margin:0 0 8px;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#64748b}
.soa-rows{list-style:none;margin:0;padding:0}
.soa-rows li{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-bottom:1px solid #f1f5f9;font-size:14px}
.soa-rows li:last-child{border-bottom:0}
.soa-rows li span:first-child{color:#334155}
.soa-rows li strong{white-space:nowrap;font-variant-numeric:tabular-nums}
.soa-rows .sub{display:block;font-size:12px;color:#64748b;font-weight:400}
.soa-share{display:inline-block;height:6px;border-radius:3px;background:#2563eb;vertical-align:middle;margin-right:6px}
/* drivers — what moves the estate */
.soa-drivers{list-style:none;margin:8px 0 0;padding:0}
.soa-drivers li{display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-bottom:1px solid #f1f5f9;font-size:14px;align-items:baseline}
.soa-drivers li strong{white-space:nowrap;font-variant-numeric:tabular-nums}
.soa-drivers .minus{color:#be123c}.soa-drivers .plus{color:#047857}.soa-drivers .neutral{color:#475569}
/* charts */
.soa-tabs{display:flex;gap:6px;flex-wrap:wrap;margin:10px 0 12px}
.soa-tabs button{appearance:none;border:2px solid #e2e8f0;background:#f8fafc;color:#334155;font-weight:700;font-size:13px;padding:8px 14px;border-radius:10px;cursor:pointer}
.soa-tabs button.active{border-color:#2563eb;background:#eff6ff;color:#1e3a8a}
.soa-chart{position:relative;border:1px solid #e2e8f0;border-radius:12px;background:#fff;padding:12px 12px 6px;margin:6px 0 10px}
.soa-chart svg{display:block;width:100%;height:auto}
.soa-chart .grid line{stroke:#e2e8f0;stroke-width:1}
.soa-chart .axis text{fill:#64748b;font-size:11px;font-family:inherit}
.soa-chart .series path{fill:none;stroke-width:2;stroke-linejoin:round;stroke-linecap:round}
.soa-chart .series circle{stroke:#fff;stroke-width:2}
.soa-chart .endlabel{font-size:12px;font-weight:700;fill:#0f172a;font-family:inherit}
.soa-chart .crosshair{stroke:#94a3b8;stroke-width:1;stroke-dasharray:3 3;display:none}
.soa-chart .hit{fill:transparent;cursor:crosshair}
.soa-legend{display:flex;gap:16px;flex-wrap:wrap;font-size:13px;color:#334155;margin:0 0 6px 4px}
.soa-legend i{display:inline-block;width:18px;height:0;border-top:3px solid currentColor;vertical-align:middle;margin-right:6px;border-radius:2px}
.soa-tip{position:absolute;pointer-events:none;display:none;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:8px 10px;font-size:12px;box-shadow:0 10px 30px rgba(15,23,42,.12);z-index:5;min-width:150px}
.soa-tip .t-title{font-weight:800;color:#0f172a;margin-bottom:4px}
.soa-tip .t-row{display:flex;justify-content:space-between;gap:12px;padding:2px 0}
.soa-tip .t-row i{display:inline-block;width:14px;border-top:3px solid currentColor;vertical-align:middle;margin-right:6px;border-radius:2px}
.soa-tip .t-row strong{font-variant-numeric:tabular-nums}
.soa-panel-chart{display:none}.soa-panel-chart.active{display:block}
/* sortable comparison table */
.soa-sort th[data-sort]{cursor:pointer;user-select:none;white-space:nowrap}
.soa-sort th[data-sort]:hover{color:#1d4ed8}
.soa-sort th[data-sort].is-sorted::after{content:' ▾';color:#2563eb}
.soa-sort tr.is-selected{background:#eff6ff}
.soa-sort .badge{display:inline-block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:999px;padding:2px 7px;margin:2px 4px 0 0;background:#fff}
/* table of contents */
.soa-toc{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:8px;margin:12px 0 4px}
.soa-toc button{appearance:none;text-align:left;border:1px solid #e2e8f0;background:#fff;border-radius:10px;padding:10px 12px;font-size:13px;font-weight:600;color:#1e293b;cursor:pointer;display:flex;gap:10px;align-items:center}
.soa-toc button:hover{border-color:#93c5fd;color:#1d4ed8}
.soa-toc button i{font-style:normal;display:inline-flex;width:22px;height:22px;border-radius:999px;background:#2563eb;color:#fff;font-size:11px;font-weight:800;align-items:center;justify-content:center;flex:none}
/* assumptions appendix */
.soa-assump{margin:10px 0;padding:0;border:1px solid #e2e8f0;border-radius:12px;background:#fff}
.soa-assump summary{padding:12px 14px;font-weight:800;color:#0f172a;list-style:none;display:flex;justify-content:space-between;align-items:center}
.soa-assump summary::after{content:'+';color:#2563eb;font-weight:800}
.soa-assump[open] summary::after{content:'–'}
.soa-assump .a-body{padding:0 14px 10px}
.soa-assump .a-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;padding:9px 0;border-bottom:1px solid #f1f5f9;font-size:13px}
.soa-assump .a-row:last-child{border-bottom:0}
.soa-assump .a-row .a-label{color:#334155}
.soa-assump .a-row .a-value{font-weight:700;text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
.soa-assump .a-row .a-note{grid-column:1 / -1;font-size:12px;color:#64748b;margin-top:-4px}
/* doing nothing against this plan (owner, 24 Oct 2026): the plan's column in navy, a pill that says what it means */
.soa-dn{width:100%;border-collapse:collapse;margin:10px 0 4px;background:#fff}
.soa-dn th,.soa-dn td{padding:9px 10px;border-bottom:1px solid #e2e8f0;text-align:left;vertical-align:middle}
.soa-dn .soa-num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
.soa-dn th.soa-dn-plan{color:#001E7A;border-bottom:2px solid #001E7A}
.soa-dn td.soa-dn-plan{color:#001E7A;font-weight:700}
.soa-pill{display:inline-block;border-radius:999px;padding:3px 10px;font-size:12.5px;font-weight:700;white-space:nowrap;font-variant-numeric:tabular-nums}
.soa-pill--up{color:#047857;background:#ecfdf5}
.soa-pill--down{color:#be123c;background:#fff1f2}
.soa-pill--none{color:#64748b;background:#f1f5f9}
/* figure boxes: one emphasised in navy where it sets the others */
.soa-figs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:8px 0 10px}
.soa-fig{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:12px 14px}
.soa-fig .label{font-size:12px;color:#475569;text-transform:none;letter-spacing:0;font-weight:600}
.soa-fig .value{font-size:22px;font-weight:800;margin-top:4px;font-variant-numeric:tabular-nums}
.soa-fig--key{border-top:4px solid #001E7A}
.soa-fig--key .value{color:#001E7A}
@media (max-width:640px){.soa-figs{grid-template-columns:1fr}.soa-dn th,.soa-dn td{padding:8px 6px}.soa-pill{white-space:normal}}
@media (max-width:860px){.soa-tiles{grid-template-columns:1fr 1fr}.soa-situ{grid-template-columns:1fr}}
/* THE PRINTED COPY (owner, 24 Oct 2026, checked as a PDF): each section starts on a new page and flows
   from its top, a heading stays with what follows it, and a chart, box or table row is never cut in two.
   Tables print at a size older readers can manage. Shared by every advice document, after its own rules. */
/* Printed as on screen even with the dialog's "background graphics" unticked (owner, 24 Oct 2026: section
   headings missing from a print to PDF — the headings are gradient text, painted by a background). */
html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
@media print{
  .h2{background:none !important;-webkit-background-clip:border-box !important;background-clip:border-box !important;color:#1e3a8a !important;-webkit-text-fill-color:#1e3a8a !important}
  .page,.page.active{break-before:page;page-break-before:always;break-inside:auto !important;page-break-inside:auto !important}
  .card{break-inside:auto !important;page-break-inside:auto !important}
  .h2,.h3,h3,h4,summary,.callout-title{break-after:avoid;page-break-after:avoid}
  p.small:last-child{break-before:avoid;page-break-before:avoid}
  .screen-only{display:none !important}
  details[data-print-closed]:not([open])>summary>*{display:none}
  details[data-print-closed]:not([open])>summary{list-style:none}
  details[data-print-closed]:not([open])>summary::-webkit-details-marker{display:none}
  details[data-print-closed]:not([open])>summary::before{content:"The full list of every strategy modelled is in the on-screen version of this document."}
  .soa-chart,.soa-panel,.soa-fig,.soa-figs,.soa-tiles,.callout,.soa-toc,tr{break-inside:avoid;page-break-inside:avoid}
  thead{display:table-header-group}
  table{font-size:12px !important}
}
@media print{.soa-tip{display:none !important}.soa-panel-chart{display:block !important}.soa-tabs{display:none !important}.soa-toc button{border:1px solid #d1d5db}.soa-assump{border:1px solid #d1d5db}}
`;

/* ─── Your situation today ──────────────────────────────────────────────────────────────────── */

export interface SoaSituation {
    clientName: string;
    partnerName: string;
    clientAge: number | null;
    partnerAge: number | null;
    isCouple: boolean;
    /** "Own home", "Renting", … per person. */
    residency: string;
    home: { value: number; ownership: string } | null;
    assetsByType: { type: string; count: number; total: number }[];
    totalAssets: number;
    liabilities: { description: string; balance: number }[];
    otherIncomes: { description: string; amount: number }[];
    livingExpenses: number;
}

const RESIDENCY_WORDS: Record<string, string> = {
    own_home: 'Own home', renting: 'Renting', with_family: 'Living with family', retirement_village: 'Retirement village', aged_care: 'Residential aged care', granny_flat: 'Granny flat',
};
const INCOME_WORDS: Record<string, string> = {
    definedBenefitPension: 'Defined benefit pension', salary: 'Salary or wages', carerAllowance: 'Carer Allowance', dvaDisability: 'DVA disability pension', dvaWarWidow: 'DVA war widow(er) pension', overseasPension: 'Overseas pension', boarders: 'Board from boarders or lodgers', other: 'Other income',
};

const ageOf = (dob: unknown): number | null => {
    if (!dob) return null;
    const d = new Date(String(dob));
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a -= 1;
    return a;
};

/** The client's position as entered — read straight off the file, nothing computed. */
export function situationFrom(factFind: DraftFactFind | null | undefined): SoaSituation {
    const fd = (factFind || {}) as DraftFactFind;
    const isCouple = fd.adviceFor === 'couple';
    const assets = (fd.assets || {}) as Record<string, Array<Record<string, unknown>> | undefined>;
    const assetsByType = Object.entries(assets)
        .map(([type, list]) => ({ type: prettify(type), count: (list || []).length, total: (list || []).reduce((t, a) => t + num(a?.value), 0) }))
        .filter((r) => r.count > 0 && r.total > 0)
        .sort((a, b) => b.total - a.total);
    const liabilities = Object.values((fd.liabilities || {}) as Record<string, Array<Record<string, unknown>> | undefined>)
        .flatMap((list) => (list || []).map((l) => ({ description: String(l?.description || prettify(String(l?.type || 'Debt'))), balance: num(l?.balance ?? l?.value) })))
        .filter((l) => l.balance > 0);
    const otherIncomes = ((fd.income?.otherIncomes || []) as Array<Record<string, unknown>>)
        .map((i) => ({ description: String(i?.description || INCOME_WORDS[String(i?.type || '')] || 'Other income'), amount: num(i?.amount) }))
        .filter((i) => i.amount > 0);
    const res = (who: 'client' | 'partner') => RESIDENCY_WORDS[String(fd[who]?.currentResidency || '')] || '';
    const residency = isCouple && res('client') !== res('partner') && res('partner')
        ? `${res('client') || 'Not recorded'} / ${res('partner')}`
        : (res('client') || 'Not recorded');
    return {
        clientName: String(fd.client?.firstName || 'Client'),
        partnerName: isCouple ? String(fd.partner?.firstName || 'Partner') : '',
        clientAge: ageOf(fd.client?.dob),
        partnerAge: isCouple ? ageOf(fd.partner?.dob) : null,
        isCouple,
        residency,
        home: num(fd.home?.value) > 0 ? { value: num(fd.home?.value), ownership: String(fd.home?.ownership || 'joint') } : null,
        assetsByType,
        totalAssets: assetsByType.reduce((t, r) => t + r.total, 0),
        liabilities,
        otherIncomes,
        livingExpenses: num(fd.expenses?.livingExpenses),
    };
}

export interface SoaTile {
    label: string;
    value: string;
    unit?: string;
    caption: string;
    tone: 'good' | 'bad' | 'family' | 'cost';
}

export function renderTiles(tiles: SoaTile[]): string {
    return `<div class="soa-tiles">${tiles.map((t) => `<div class="soa-tile soa-tile--${t.tone}"><div class="t-label">${esc(t.label)}</div><div class="t-value">${esc(t.value)}${t.unit ? `<span class="t-unit">${esc(t.unit)}</span>` : ''}</div><div class="t-cap">${esc(t.caption)}</div></div>`).join('')}</div>`;
}

/**
 * "Your situation today": the four numbers of the baseline, then the household, what is owned
 * (by type, each with its share of the total), what comes in and goes out, and what is owed.
 */
export function renderSituationHtml(s: SoaSituation, baseline: { name: string; pension: number; cashflow: number; estate: number; netWorth?: number | null }, years: number, bridge?: string | null): string {
    const household = s.isCouple
        ? `${esc(s.clientName)}${s.clientAge != null ? ` (${s.clientAge})` : ''} and ${esc(s.partnerName)}${s.partnerAge != null ? ` (${s.partnerAge})` : ''}`
        : `${esc(s.clientName)}${s.clientAge != null ? ` (${s.clientAge})` : ''}`;
    const netWorth = baseline.netWorth ?? (s.totalAssets + (s.home?.value ?? 0) - s.liabilities.reduce((t, l) => t + l.balance, 0));
    const tiles = renderTiles([
        { label: 'Age Pension today', value: money(Math.round(baseline.pension)), unit: 'a year', caption: baseline.pension > 0 ? 'Estimated entitlement on today’s assets and income.' : 'No entitlement on today’s assets and income.', tone: 'good' },
        { label: baseline.cashflow < 0 ? 'Left to find each year' : 'Left over each year', value: `${baseline.cashflow < 0 ? '−' : '+'}${money(Math.abs(Math.round(baseline.cashflow)))}`, caption: baseline.cashflow < 0 ? 'Money in, less money out. Met from savings.' : 'Money in, less money out, as things stand.', tone: baseline.cashflow < 0 ? 'bad' : 'good' },
        { label: 'Net worth today', value: money(Math.round(netWorth)), caption: s.home ? 'Everything owned, less everything owed — the home included.' : 'Everything owned, less everything owed.', tone: 'cost' },
        { label: "What's left to the family", value: money(Math.round(baseline.estate)), caption: `After ${years} ${years === 1 ? 'year' : 'years'}, ${baseline.name.toLowerCase()}.`, tone: 'family' },
    ]);
    const max = Math.max(1, ...s.assetsByType.map((r) => r.total));
    const assetRows = s.assetsByType.length
        ? s.assetsByType.map((r) => `<li><span><i class="soa-share" style="width:${Math.max(6, Math.round((r.total / max) * 70))}px"></i>${esc(r.type)}<span class="sub">${r.count} ${r.count === 1 ? 'item' : 'items'} · ${s.totalAssets > 0 ? Math.round((r.total / s.totalAssets) * 100) : 0}% of investments</span></span><strong>${money(r.total)}</strong></li>`).join('')
        : '<li><span class="muted">No investments or savings recorded.</span></li>';
    const incomeRows = s.otherIncomes.length
        ? s.otherIncomes.map((i) => `<li><span>${esc(i.description)}</span><strong>${money(i.amount)} a year</strong></li>`).join('')
        : '<li><span class="muted">No income other than the pension and what the investments earn.</span></li>';
    const debtRows = s.liabilities.length
        ? s.liabilities.map((l) => `<li><span>${esc(l.description)}</span><strong>${money(l.balance)}</strong></li>`).join('')
        : '<li><span class="muted">No debts recorded.</span></li>';
    // The sentence that bridges today's position to the basis the strategies are measured on
    // (the aged care document: the in-care do-nothing run; owner, 9 Oct 2026).
    // **…** marks the figures compared against, in bold (escaped first, so nothing else becomes markup).
    const bridgeHtml = bridge ? `<p class="lede">${esc(bridge).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</p>` : '';
    return `${tiles}${bridgeHtml}
<div class="soa-situ">
<div class="soa-panel"><h4>Your household</h4><ul class="soa-rows">
<li><span>${s.isCouple ? 'Couple' : 'Single'}</span><strong>${household}</strong></li>
<li><span>Where you live</span><strong>${esc(s.residency)}</strong></li>
${s.home ? `<li><span>The home<span class="sub">${esc(prettify(s.home.ownership))} · exempt from the Age Pension test while you live in it</span></span><strong>${money(s.home.value)}</strong></li>` : '<li><span>The home</span><strong>No home recorded</strong></li>'}
<li><span>Living expenses</span><strong>${money(s.livingExpenses)} a year</strong></li>
</ul></div>
<div class="soa-panel"><h4>What you own, apart from the home</h4><ul class="soa-rows">${assetRows}${s.assetsByType.length ? `<li><span><strong>Total investments and savings</strong></span><strong>${money(s.totalAssets)}</strong></li>` : ''}</ul></div>
<div class="soa-panel"><h4>Money coming in, apart from the pension</h4><ul class="soa-rows">${incomeRows}</ul></div>
<div class="soa-panel"><h4>What you owe</h4><ul class="soa-rows">${debtRows}</ul></div>
</div>`;
}

/* ─── Charts ────────────────────────────────────────────────────────────────────────────────── */

export interface SoaChartSeries {
    name: string;
    values: number[];
    /** Index into SOA_SERIES_COLORS. */
    color?: number;
}

const niceStep = (span: number): number => {
    const raw = span / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(1, raw))));
    const norm = raw / mag;
    const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
    return step * mag;
};
const short = (n: number): string => {
    const a = Math.abs(n);
    const s = a >= 1_000_000 ? `$${(a / 1_000_000).toFixed(a % 1_000_000 === 0 ? 0 : 1)}m` : a >= 1_000 ? `$${Math.round(a / 1_000)}k` : `$${Math.round(a)}`;
    return n < 0 ? `−${s}` : s;
};

/**
 * One line chart: categories along x, money on y, up to three series. Markers are 9px with a 2px
 * surface ring; the last point of each series is labelled directly; a legend shows only for two
 * or more series. The hover layer (crosshair + one tooltip for every series) is wired by the
 * booklet script from `data-chart`.
 */
export function renderLineChart(opts: { id: string; title: string; categories: string[]; series: SoaChartSeries[]; note?: string }): string {
    const W = 760, H = 300, padL = 64, padR = 110, padT = 18, padB = 36;
    const innerW = W - padL - padR, innerH = H - padT - padB;
    const all = opts.series.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
    if (!all.length || opts.categories.length < 2) return '';
    /*
     * The axis starts at zero where the data crosses it or spans most of its own size. Where every
     * value sits in a narrow band far from zero (a $5m estate moving by $200k), a zero axis would
     * flatten the two lines into one — so the band is padded instead and the chart says so.
     */
    const vLo = Math.min(...all), vHi = Math.max(...all);
    const span = vHi - vLo;
    const zoomed = vLo > 0 && span < 0.5 * vHi;
    let lo = zoomed ? vLo - span * 0.25 : Math.min(0, vLo);
    let hi = zoomed ? vHi + span * 0.25 : Math.max(0, vHi);
    if (hi === lo) hi = lo + 1;
    const step = niceStep(hi - lo);
    lo = Math.floor(lo / step) * step;
    hi = Math.ceil(hi / step) * step;
    const n = opts.categories.length;
    const x = (i: number) => padL + (innerW * i) / (n - 1);
    const y = (v: number) => padT + innerH - ((v - lo) / (hi - lo)) * innerH;
    const ticks: number[] = [];
    for (let t = lo; t <= hi + step / 2; t += step) ticks.push(t);
    const grid = ticks.map((t) => `<line x1="${padL}" x2="${W - padR}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}"${t === 0 ? ' style="stroke:#94a3b8"' : ''}/><text x="${padL - 8}" y="${(y(t) + 4).toFixed(1)}" text-anchor="end">${esc(short(t))}</text>`).join('');
    const every = n > 12 ? Math.ceil(n / 12) : 1;
    const xLabels = opts.categories.map((c, i) => (i % every === 0 || i === n - 1) ? `<text x="${x(i).toFixed(1)}" y="${H - padB + 18}" text-anchor="middle">${esc(c)}</text>` : '').join('');
    // End labels never sit on each other: ordered by height, each is pushed below the one above.
    const labelY = opts.series.map((s) => y(s.values[s.values.length - 1] ?? 0) + 4);
    const order = labelY.map((v, i) => [v, i] as const).sort((a, b) => a[0] - b[0]);
    for (let k = 1; k < order.length; k += 1) {
        const prev = order[k - 1]!, cur = order[k]!;
        if (cur[0] - prev[0] < 14) { labelY[cur[1]] = prev[0] + 14; order[k] = [prev[0] + 14, cur[1]]; }
    }
    // Drawn last-to-first so the first series (the plan) sits on top where lines coincide.
    const seriesSvg = opts.series.map((s, si) => {
        const color = SOA_SERIES_COLORS[s.color ?? si] ?? SOA_SERIES_COLORS[0];
        const pts = s.values.map((v, i) => [x(i), y(v)] as const);
        const d = pts.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`).join(' ');
        const last = pts[pts.length - 1]!;
        const circles = pts.map(([px, py], i) => `<circle class="pt" data-i="${i}" cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="4.5" fill="${color}"/>`).join('');
        return `<g class="series" data-series="${si}" style="color:${color}"><path d="${d}" stroke="${color}"/>${circles}<text class="endlabel" x="${(last[0] + 10).toFixed(1)}" y="${(labelY[si] ?? last[1] + 4).toFixed(1)}">${esc(short(s.values[s.values.length - 1] ?? 0))}</text></g>`;
    }).reverse().join('');
    const legend = opts.series.length > 1
        ? `<div class="soa-legend">${opts.series.map((s, si) => `<span style="color:${SOA_SERIES_COLORS[s.color ?? si] ?? SOA_SERIES_COLORS[0]}"><i></i><span style="color:#334155">${esc(s.name)}</span></span>`).join('')}</div>`
        : '';
    const data = esc(JSON.stringify({ categories: opts.categories, series: opts.series.map((s, si) => ({ name: s.name, values: s.values, color: SOA_SERIES_COLORS[s.color ?? si] ?? SOA_SERIES_COLORS[0] })), padL, padR, W, title: opts.title }));
    return `<div class="soa-chart" id="${esc(opts.id)}" data-chart="${data}">
<p class="small" style="margin:0 0 4px;font-weight:700;color:#0f172a">${esc(opts.title)}</p>${legend}
<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(opts.title)}">
<g class="grid">${grid}</g><g class="axis">${xLabels}</g>
${seriesSvg}
<line class="crosshair" y1="${padT}" y2="${H - padB}" x1="0" x2="0"/>
<rect class="hit" x="${padL}" y="${padT}" width="${innerW}" height="${innerH}"/>
</svg>
<div class="soa-tip"></div>
${opts.note || zoomed ? `<p class="tiny muted" style="margin:6px 0 4px">${esc(opts.note || '')}${zoomed ? `${opts.note ? ' ' : ''}The axis starts at ${esc(short(lo))}, not zero, so the difference between the lines can be seen.` : ''}</p>` : ''}
</div>`;
}

export interface SoaProjectionSeriesRow {
    label: string;
    pension: number;
    cashflow: number;
    netWorth: number;
}

/**
 * The three measures as tabs — net worth, Age Pension, cashflow — the plan against the baseline
 * where one was projected. One axis per chart (never two scales on one picture).
 */
export function renderProjectionCharts(opts: { idPrefix: string; planName: string; plan: SoaProjectionSeriesRow[]; baselineName?: string | null; baseline?: SoaProjectionSeriesRow[] | null }): string {
    if (!opts.plan.length) return '';
    const categories = opts.plan.map((r) => r.label);
    const base = opts.baseline && opts.baseline.length >= 2 ? opts.baseline.slice(0, categories.length) : null;
    const seriesFor = (pick: (r: SoaProjectionSeriesRow) => number): SoaChartSeries[] => [
        { name: opts.planName, values: opts.plan.map(pick), color: 0 },
        ...(base ? [{ name: opts.baselineName || 'Baseline', values: base.map(pick), color: 1 }] : []),
    ];
    const tabs: Array<{ key: string; label: string; title: string; pick: (r: SoaProjectionSeriesRow) => number; note: string }> = [
        { key: 'networth', label: 'Net worth', title: 'Net worth at the end of each year', pick: (r) => r.netWorth, note: 'Everything owned less everything owed. Money drawn to cover a shortfall shows here as a flatter or falling line.' },
        { key: 'pension', label: 'Age Pension', title: 'Age Pension each year', pick: (r) => r.pension, note: 'Rates and thresholds are indexed from year 2 at the projection inflation rate.' },
        { key: 'cashflow', label: 'Cashflow', title: 'Money in less money out, each year', pick: (r) => r.cashflow, note: 'Below the line, the year’s shortfall is drawn from savings.' },
    ];
    return `<div class="soa-tabset" data-tabset="${esc(opts.idPrefix)}">
<div class="soa-tabs" role="tablist">${tabs.map((t, i) => `<button type="button" role="tab" class="${i === 0 ? 'active' : ''}" data-tab="${esc(opts.idPrefix)}-${t.key}">${esc(t.label)}</button>`).join('')}</div>
${tabs.map((t, i) => `<div class="soa-panel-chart${i === 0 ? ' active' : ''}" data-panel="${esc(opts.idPrefix)}-${t.key}">${renderLineChart({ id: `${opts.idPrefix}-${t.key}-chart`, title: t.title, categories, series: seriesFor(t.pick), note: t.note })}</div>`).join('')}
</div>`;
}

/* ─── The comparison table ──────────────────────────────────────────────────────────────────── */

export interface SoaComparisonRow {
    name: string;
    description?: string;
    pension: number;
    cashflow: number;
    estate: number;
    selected: boolean;
    badges: string[];
    /** A what-if, not a plan — shown in its own group. */
    sensitivity?: boolean;
}

/** Every strategy modelled, sortable by any figure (the booklet script wires the headers). */
export function renderComparisonTable(rows: SoaComparisonRow[], years: number, baseline: { name: string; pension: number; cashflow: number; estate: number } | null): string {
    const row = (r: SoaComparisonRow, cls = '') => `<tr class="${cls}${r.selected ? ' is-selected' : ''}" data-selected="${r.selected ? 1 : 0}">
<td><div style="font-weight:700;color:#0f172a">${esc(r.name)}${r.selected ? ' <span class="badge" style="color:#047857;border-color:#a7f3d0">Your choice</span>' : ''}</div>${r.badges.map((b) => `<span class="badge">${esc(b)}</span>`).join('')}${r.description ? `<div class="tiny muted" style="margin-top:4px;max-width:520px">${esc(r.description)}</div>` : ''}</td>
<td class="td-num soa-num" data-value="${r.pension}">${money(r.pension)}</td>
<td class="td-num soa-num ${r.cashflow < 0 ? 'age-neg' : ''}" data-value="${r.cashflow}">${money(r.cashflow)}</td>
<td class="td-num soa-num" data-value="${r.estate}"><strong>${money(r.estate)}</strong></td>
</tr>`;
    const plans = rows.filter((r) => !r.sensitivity);
    const whatIfs = rows.filter((r) => r.sensitivity);
    return `<table class="soa-sort" data-sortable>
<thead><tr><th>Strategy</th><th data-sort="1" style="text-align:right">Age Pension a year</th><th data-sort="2" style="text-align:right">Cashflow a year</th><th data-sort="3" class="is-sorted" style="text-align:right">Estate after ${years} years</th></tr></thead>
<tbody>${baseline ? `<tr class="is-baseline"><td><div style="font-weight:700;color:#0f172a">${esc(baseline.name)}</div><div class="tiny muted">The benchmark every strategy is measured against.</div></td><td class="td-num soa-num" data-value="${baseline.pension}">${money(baseline.pension)}</td><td class="td-num soa-num ${baseline.cashflow < 0 ? 'age-neg' : ''}" data-value="${baseline.cashflow}">${money(baseline.cashflow)}</td><td class="td-num soa-num" data-value="${baseline.estate}"><strong>${money(baseline.estate)}</strong></td></tr>` : ''}${plans.map((r) => row(r)).join('')}</tbody>
${whatIfs.length ? `<tbody class="soa-whatifs"><tr><td colspan="4" style="padding-top:16px"><div class="h3" style="margin:0 0 2px">What if the price were different?</div><div class="tiny muted">Modelled to show what the price itself does. Not a recommendation.</div></td></tr>${whatIfs.map((r) => row(r, 'is-whatif')).join('')}</tbody>` : ''}
</table>`;
}

/* ─── Doing nothing against this plan ───────────────────────────────────────────────────────── */

/** Which way is better for each measure, and the words for the change (owner, 24 Oct 2026). */
export type SoaDoNothingMeasure = 'estate' | 'pension' | 'cashflow' | 'fees' | 'contribution';
export interface SoaDoNothingRow {
    measure: SoaDoNothingMeasure;
    label: string;
    /** Either figure can be missing on a document saved before both were carried; the change still reads. */
    doNothing: number | null;
    plan: number | null;
    /** Plan less doing nothing. */
    delta: number;
}

const DO_NOTHING_WORDS: Record<SoaDoNothingMeasure, { higherIsBetter: boolean; up: string; down: string }> = {
    estate: { higherIsBetter: true, up: 'more to the family', down: 'less to the family' },
    pension: { higherIsBetter: true, up: 'a year more Age Pension', down: 'a year less Age Pension' },
    cashflow: { higherIsBetter: true, up: 'a year better off', down: 'a year worse off' },
    fees: { higherIsBetter: false, up: 'a year less in fees', down: 'a year more in fees' },
    contribution: { higherIsBetter: false, up: 'a year less in contributions', down: 'a year more in contributions' },
};

/** "$X more to the family" in green, "$X a year more in fees" in red, "no change" in grey. */
export function doNothingPill(measure: SoaDoNothingMeasure, delta: number): string {
    const d = Math.round(Number(delta) || 0);
    if (d === 0) return '<span class="soa-pill soa-pill--none">no change</span>';
    const w = DO_NOTHING_WORDS[measure];
    const better = w.higherIsBetter ? d > 0 : d < 0;
    return `<span class="soa-pill soa-pill--${better ? 'up' : 'down'}">${better ? '▲' : '▼'} ${money(Math.abs(d))} ${better ? w.up : w.down}</span>`;
}

/** Each measure: what doing nothing gives, what this plan gives, and what that means for the family. */
/** `baseLabel`: what the plan is compared with, in the module's words ("Paying daily", "The support alone"). */
export function renderDoNothingTable(rows: SoaDoNothingRow[], baseLabel = 'The comparison'): string {
    const fig = (n: number | null) => (n == null || !Number.isFinite(n) ? '—' : `${n < 0 ? '−' : ''}${money(Math.abs(Math.round(n)))}`);
    return `<table class="soa-dn">
<thead><tr><th>Measure</th><th class="soa-num">${esc(baseLabel)}</th><th class="soa-num soa-dn-plan">This plan</th><th>What it means for you</th></tr></thead>
<tbody>${rows.map((r) => `<tr><td>${esc(r.label)}</td><td class="soa-num">${fig(r.doNothing)}</td><td class="soa-num soa-dn-plan">${fig(r.plan)}</td><td>${doNothingPill(r.measure, r.delta)}</td></tr>`).join('')}</tbody>
</table>`;
}

/* ─── Table of contents ─────────────────────────────────────────────────────────────────────── */

export function renderToc(titles: string[]): string {
    return `<div class="soa-toc">${titles.map((t, i) => `<button type="button" data-goto="${i}"><i>${i + 1}</i><span>${esc(t)}</span></button>`).join('')}</div>`;
}

/* ─── The assumptions appendix ──────────────────────────────────────────────────────────────── */

/** The Assumptions page's sections as collapsible groups; those matching `openWhen` start open. */
export function renderAssumptionsAppendixHtml(sections: AssumptionSection[], openWhen: RegExp): string {
    return sections.map((sec) => `<details class="soa-assump"${openWhen.test(sec.title) ? ' open' : ''}><summary>${esc(sec.title)}</summary><div class="a-body">
${sec.items.map((it) => `<div class="a-row"><div class="a-label">${esc(it.label)}</div><div class="a-value">${esc(it.value)}</div>${it.note ? `<div class="a-note">${esc(it.note)}</div>` : ''}${it.details ? `<div class="a-note">${esc(it.details)}</div>` : ''}</div>`).join('')}
</div></details>`).join('');
}

export { esc as soaEscape };
